import type { MarkerStyle } from './marker-manager';

/**
 * Named marker presets for the common gameplay scenarios. Game modes import
 * by name (`markerStyles.click`) instead of redefining a `MarkerStyle`
 * object every place a marker is dropped — keeps the visual language
 * consistent across modes and gives the design system one place to retune.
 *
 * Color choices align with the design tokens (docs/map-engine-design.md
 * § 11) so click/location/correct/incorrect all read as part of the same
 * surface as borders, fills, and the rest of the UI.
 *
 * Sizes are in world units (sprite size in `MarkerStyle`); 0.012-0.020
 * is the readable range at default camera distance. All include a thin
 * dark border for legibility against varied texture / fill backdrops.
 */
const DARK_BORDER = { color: 'rgba(40, 25, 10, 0.75)', widthRatio: 0.08 };

/**
 * Border color matching the country borders mesh (`--ink` cream). Used
 * by the country-pin dots so the dot reads as "part of that country's
 * outline" rather than a foreign overlay sprite.
 */
const COUNTRY_BORDER = { color: '#e8e4d8', widthRatio: 0.12 };

/**
 * Where the user clicked. Cyan, pin-style (3D dot + tangent ring) so it
 * reads as a surface anchor — the ring foreshortens at horizons and the
 * pattern is visually distinct from the small-country dot sprites.
 */
const click: MarkerStyle = {
  shape: 'pin',
  fill: '#5fb6cd',
  size: 0.024,
  hoverFill: '#9fd5e3',
};

/**
 * The correct location revealed after a guess. Gold, slightly larger than
 * the click marker so it reads as the "answer" anchor when both are visible
 * with an arc between them. Pin-style.
 */
const location: MarkerStyle = {
  shape: 'pin',
  fill: '#d4a24c',
  size: 0.028,
  hoverFill: '#f4c772',
};

/**
 * Past guesses for the current target — across rounds OR across past
 * sessions (City Practice's per-city history surface). Soft lavender,
 * dimmer than `click`, so the current round's markers stay visually
 * dominant. Color ported from the reference prototype.
 */
const history: MarkerStyle = {
  shape: 'pin',
  fill: '#b794f4',
  size: 0.018,
  pinRingOpacity: 0.5,
  hoverFill: '#d3b7ff',
};

/**
 * Right-on-the-money result. Same green as the `correct` fill state so
 * the connection across surfaces is obvious. Pin-style.
 */
const correct: MarkerStyle = {
  shape: 'pin',
  fill: '#7fb069',
  size: 0.026,
  hoverFill: '#a3c98f',
};

/**
 * Wrong-answer result. Same warm-orange as the `incorrect` fill state. Pin-style.
 */
const incorrect: MarkerStyle = {
  shape: 'pin',
  fill: '#d97757',
  size: 0.026,
  hoverFill: '#e89779',
};

/**
 * Auto-placed dot for tiny countries (Vatican, Monaco, Tuvalu, etc.)
 * in tap-the-country modes. Default fill matches the country-fill base
 * token so unselected dots read as a continuation of the polygon
 * surface; cream border matches the country-border line color so the
 * dot looks like a tiny country outline.
 */
const countryPin: MarkerStyle = {
  fill: '#4a5568',
  size: 0.015,
  border: COUNTRY_BORDER,
  hoverFill: '#7589a8',
};

/**
 * Small-country dot in the "correct guess" state — green fill, same
 * cream border as the neutral pin. Mirrors the polygon `correct` fill
 * color so dot-countries highlight identically to large polygon
 * countries on a right answer.
 */
const countryPinCorrect: MarkerStyle = {
  fill: '#7fb069',
  size: 0.015,
  border: COUNTRY_BORDER,
};

/**
 * Small-country dot in the "wrong guess" state — warm-orange fill
 * matching the polygon `incorrect` color.
 */
const countryPinIncorrect: MarkerStyle = {
  fill: '#d97757',
  size: 0.015,
  border: COUNTRY_BORDER,
};

/**
 * Small-country dot in the "revealed" state — cyan, matching the same
 * cyan used for the `selected` polygon fill so dot reveals visually
 * tie to polygon reveals. Used by the country quiz when the player
 * exhausts their attempts and the answer is shown.
 */
const countryPinRevealed: MarkerStyle = {
  fill: '#5fb6cd',
  size: 0.015,
  border: COUNTRY_BORDER,
};

/**
 * City dot for capital cities — slightly brighter than `cityMinor`,
 * meant for "name this capital" modes where capitals are the focus.
 */
const cityCapital: MarkerStyle = {
  fill: '#f4c772',
  size: 0.014,
  border: DARK_BORDER,
  hoverFill: '#ffe5a8',
};

/**
 * Generic city dot — minor cities, populated places. Cream/neutral so
 * many can be on screen without competing visually.
 */
const cityMinor: MarkerStyle = {
  fill: '#e8e4d8',
  size: 0.011,
  border: DARK_BORDER,
  hoverFill: '#ffffff',
};

/**
 * Public preset registry. Game modes use `markerStyles.click` etc.
 * Frozen so a typo on read becomes a TS error and runtime mutation
 * doesn't leak across modes.
 */
export const markerStyles = Object.freeze({
  click,
  location,
  history,
  correct,
  incorrect,
  countryPin,
  countryPinCorrect,
  countryPinIncorrect,
  countryPinRevealed,
  cityCapital,
  cityMinor,
});

/** Convenience type for the preset names — useful for typed lookups. */
export type MarkerPresetName = keyof typeof markerStyles;
